// SPDX-License-Identifier: AGPL-3.0-or-later
import {Logger} from '@app/features/platform/utils/AppLogger';
import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';
import type {NativeAudioAvailability, NativeAudioStartResult} from '@app/types/electron.d';

const logger = new Logger('NativeAudioCaptureBridge');
const MAX_GENERATOR_PENDING_AUDIO_FRAMES = 16;
interface ArmedNativeAudioCapture {
	captureId: string;
}
interface NativeAudioBridgeHandle {
	track: MediaStreamTrack;
	cleanup: (stopRemote?: boolean) => Promise<void>;
}
interface GeneratorAudioTrack extends MediaStreamTrack {
	writable: WritableStream<unknown>;
}
interface AudioDataCtor {
	new (init: {
		data: BufferSource;
		format: string;
		sampleRate: number;
		numberOfFrames: number;
		numberOfChannels: number;
		timestamp: number;
	}): AudioDataFrame;
}
interface GeneratorCtor {
	new (options: {kind: 'audio'}): GeneratorAudioTrack;
}
interface AudioDataFrame {
	close: () => void;
}
let patched = false;
let originalGetDisplayMedia: MediaDevices['getDisplayMedia'] | null = null;
let availabilityCache: Promise<NativeAudioAvailability> | null = null;
let armedCapture: ArmedNativeAudioCapture | null = null;
let activeBridge: {
	captureId: string;
	handle: NativeAudioBridgeHandle;
} | null = null;
export interface NativeAudioBridgeStats {
	active: boolean;
	bridgeMode: 'generator' | 'script-processor' | null;
	captureId: string | null;
	startedAt: number | null;
	lastFrameAt: number | null;
	framesReceived: number;
	framesDropped: number;
	endReason: string | null;
	endDetail: string | null;
	endedAt: number | null;
}
const initialBridgeStats: NativeAudioBridgeStats = {
	active: false,
	bridgeMode: null,
	captureId: null,
	startedAt: null,
	lastFrameAt: null,
	framesReceived: 0,
	framesDropped: 0,
	endReason: null,
	endDetail: null,
	endedAt: null,
};
let bridgeStats: NativeAudioBridgeStats = {...initialBridgeStats};
export function getNativeAudioBridgeStats(): NativeAudioBridgeStats {
	return {...bridgeStats};
}
function startBridgeStats(mode: 'generator' | 'script-processor', captureId: string): void {
	bridgeStats = {
		...initialBridgeStats,
		active: true,
		bridgeMode: mode,
		captureId,
		startedAt: Date.now(),
	};
}
function recordBridgeFrame(): void {
	if (!bridgeStats.active) return;
	bridgeStats.framesReceived += 1;
	bridgeStats.lastFrameAt = Date.now();
}
function recordBridgeFrameDrops(count: number): void {
	if (!bridgeStats.active || count <= 0) return;
	bridgeStats.framesDropped += count;
}
function endBridgeStats(reason: string | null, detail: string | null): void {
	if (!bridgeStats.active) return;
	bridgeStats.active = false;
	bridgeStats.endReason = reason;
	bridgeStats.endDetail = detail;
	bridgeStats.endedAt = Date.now();
}
function isNativeAudioDesktopPlatform(): boolean {
	const electronApi = getElectronAPI();
	return Boolean(electronApi) && (electronApi?.platform === 'darwin' || electronApi?.platform === 'win32');
}
function getGeneratorCtor(): GeneratorCtor | undefined {
	return (
		window as typeof window & {
			MediaStreamTrackGenerator?: GeneratorCtor;
		}
	).MediaStreamTrackGenerator;
}
function getAudioDataCtor(): AudioDataCtor | undefined {
	return (
		window as typeof window & {
			AudioData?: AudioDataCtor;
		}
	).AudioData;
}
function getNativeAudioApi() {
	return getElectronAPI()?.nativeAudio ?? null;
}
function isArrayBuffer(value: unknown): value is ArrayBuffer {
	return value instanceof ArrayBuffer || Object.prototype.toString.call(value) === '[object ArrayBuffer]';
}
function isValidAudioFrameMessage(message: {
	sampleRate: number;
	channels: number;
	timestampUs: number;
	samples: ArrayBuffer;
}): boolean {
	return (
		isArrayBuffer(message.samples) &&
		Number.isFinite(message.sampleRate) &&
		message.sampleRate > 0 &&
		Number.isSafeInteger(message.channels) &&
		message.channels > 0 &&
		Number.isFinite(message.timestampUs) &&
		message.samples.byteLength % Float32Array.BYTES_PER_ELEMENT === 0 &&
		message.samples.byteLength / Float32Array.BYTES_PER_ELEMENT >= message.channels &&
		(message.samples.byteLength / Float32Array.BYTES_PER_ELEMENT) % message.channels === 0
	);
}
async function replaceStreamAudioTrack(stream: MediaStream, nextTrack: MediaStreamTrack): Promise<void> {
	for (const existingAudioTrack of stream.getAudioTracks()) {
		stream.removeTrack(existingAudioTrack);
		try {
			existingAudioTrack.stop();
		} catch {}
	}
	stream.addTrack(nextTrack);
}
function attachNativeAudioCleanup(stream: MediaStream, captureId: string, handle: NativeAudioBridgeHandle): void {
	const videoTrack = stream.getVideoTracks()[0];
	if (!videoTrack) return;
	const cleanup = (): void => {
		if (activeBridge?.captureId === captureId) {
			activeBridge = null;
		}
		void handle.cleanup(true);
		videoTrack.removeEventListener('ended', cleanup);
	};
	videoTrack.addEventListener('ended', cleanup);
}
async function createGeneratorBridge(captureId: string): Promise<NativeAudioBridgeHandle> {
	const nativeAudioApi = getNativeAudioApi();
	const Generator = getGeneratorCtor();
	const AudioData = getAudioDataCtor();
	if (!nativeAudioApi || !Generator || !AudioData) {
		throw new Error('MediaStreamTrackGenerator path unavailable');
	}
	const generator = new Generator({kind: 'audio'});
	const writer = generator.writable.getWriter();
	let cleanedUp = false;
	let activeWrite: Promise<void> | null = null;
	const pendingFrames: Array<AudioDataFrame> = [];
	startBridgeStats('generator', captureId);
	const closeAudioFrame = (audioFrame: AudioDataFrame): void => {
		try {
			audioFrame.close();
		} catch {}
	};
	const pumpPendingFrames = (): void => {
		if (cleanedUp || activeWrite || pendingFrames.length === 0) return;
		const audioFrame = pendingFrames.shift()!;
		try {
			activeWrite = writer
				.write(audioFrame)
				.catch((error) => {
					logger.warn('Failed to write native audio frame to MediaStreamTrackGenerator', {
						captureId,
						error,
					});
				})
				.finally(() => {
					closeAudioFrame(audioFrame);
					activeWrite = null;
					pumpPendingFrames();
				});
		} catch (error) {
			logger.warn('Failed to write native audio frame to MediaStreamTrackGenerator', {captureId, error});
			closeAudioFrame(audioFrame);
			activeWrite = null;
			pumpPendingFrames();
		}
	};
	const cleanup = async (stopRemote: boolean = true): Promise<void> => {
		if (cleanedUp) return;
		cleanedUp = true;
		endBridgeStats('cleanup', stopRemote ? 'caller-stopped' : 'remote-ended');
		unsubscribeFrame();
		unsubscribeEnd();
		for (const audioFrame of pendingFrames.splice(0)) {
			closeAudioFrame(audioFrame);
		}
		const stopRemotePromise = stopRemote
			? nativeAudioApi.stop(captureId).catch((error) => {
					logger.warn('Failed to stop native audio capture after generator cleanup', {captureId, error});
				})
			: null;
		if (activeWrite) {
			await activeWrite;
		}
		try {
			await writer.close();
		} catch {}
		try {
			generator.stop();
		} catch {}
		await stopRemotePromise;
	};
	const unsubscribeFrame = nativeAudioApi.onFrame((message) => {
		if (message.captureId !== captureId || cleanedUp) return;
		if (!isValidAudioFrameMessage(message)) {
			logger.warn('Dropping invalid native audio frame', {
				captureId,
				sampleRate: message.sampleRate,
				channels: message.channels,
				byteLength: message.samples?.byteLength,
				timestampUs: message.timestampUs,
			});
			recordBridgeFrameDrops(1);
			return;
		}
		recordBridgeFrame();
		const samples = new Float32Array(message.samples);
		const numberOfFrames = Math.floor(samples.length / message.channels);
		const audioFrame = new AudioData({
			data: samples,
			format: 'f32',
			sampleRate: message.sampleRate,
			numberOfFrames,
			numberOfChannels: message.channels,
			timestamp: Math.max(0, Math.round(message.timestampUs)),
		});
		pendingFrames.push(audioFrame);
		let dropped = 0;
		while (pendingFrames.length > MAX_GENERATOR_PENDING_AUDIO_FRAMES) {
			const droppedFrame = pendingFrames.shift();
			if (droppedFrame) closeAudioFrame(droppedFrame);
			dropped += 1;
		}
		recordBridgeFrameDrops(dropped);
		pumpPendingFrames();
	});
	const unsubscribeEnd = nativeAudioApi.onEnd((message) => {
		if (message.captureId !== captureId) return;
		endBridgeStats(message.reason ?? 'ended', message.detail ?? null);
		void cleanup(false);
	});
	return {
		track: generator,
		cleanup,
	};
}
async function createScriptProcessorBridge(captureId: string): Promise<NativeAudioBridgeHandle> {
	const nativeAudioApi = getNativeAudioApi();
	if (!nativeAudioApi) {
		throw new Error('Native audio API unavailable');
	}
	const AudioContextCtor = window.AudioContext ?? window.webkitAudioContext;
	if (!AudioContextCtor) {
		throw new Error('AudioContext unavailable');
	}
	const audioContext = new AudioContextCtor({sampleRate: 48000});
	const destination = audioContext.createMediaStreamDestination();
	const processor = audioContext.createScriptProcessor(2048, 0, 2);
	const mutedSink = audioContext.createGain();
	startBridgeStats('script-processor', captureId);
	mutedSink.gain.value = 0;
	processor.connect(destination);
	processor.connect(mutedSink);
	mutedSink.connect(audioContext.destination);
	if (audioContext.state === 'suspended') {
		void audioContext.resume().catch(() => {});
	}
	const pendingChunks: Array<Float32Array> = [];
	let chunkOffset = 0;
	let cleanedUp = false;
	processor.onaudioprocess = (event) => {
		const left = event.outputBuffer.getChannelData(0);
		const right = event.outputBuffer.getChannelData(1);
		left.fill(0);
		right.fill(0);
		let outputIndex = 0;
		while (outputIndex < left.length && pendingChunks.length > 0) {
			const chunk = pendingChunks[0]!;
			const availableFrames = Math.floor((chunk.length - chunkOffset) / 2);
			const framesToCopy = Math.min(left.length - outputIndex, availableFrames);
			for (let frameIndex = 0; frameIndex < framesToCopy; frameIndex++) {
				left[outputIndex + frameIndex] = chunk[chunkOffset + frameIndex * 2] ?? 0;
				right[outputIndex + frameIndex] = chunk[chunkOffset + frameIndex * 2 + 1] ?? 0;
			}
			outputIndex += framesToCopy;
			chunkOffset += framesToCopy * 2;
			if (chunkOffset >= chunk.length) {
				pendingChunks.shift();
				chunkOffset = 0;
			}
		}
	};
	const cleanup = async (stopRemote: boolean = true): Promise<void> => {
		if (cleanedUp) return;
		cleanedUp = true;
		endBridgeStats('cleanup', stopRemote ? 'caller-stopped' : 'remote-ended');
		unsubscribeFrame();
		unsubscribeEnd();
		processor.onaudioprocess = null;
		processor.disconnect();
		mutedSink.disconnect();
		for (const track of destination.stream.getTracks()) {
			track.stop();
		}
		await audioContext.close().catch(() => {});
		if (stopRemote) {
			try {
				await nativeAudioApi.stop(captureId);
			} catch (error) {
				logger.warn('Failed to stop native audio capture after script-processor cleanup', {
					captureId,
					error,
				});
			}
		}
	};
	const unsubscribeFrame = nativeAudioApi.onFrame((message) => {
		if (message.captureId !== captureId || cleanedUp) return;
		if (!isValidAudioFrameMessage(message)) {
			logger.warn('Dropping invalid native audio frame', {
				captureId,
				sampleRate: message.sampleRate,
				channels: message.channels,
				byteLength: message.samples?.byteLength,
				timestampUs: message.timestampUs,
			});
			recordBridgeFrameDrops(1);
			return;
		}
		recordBridgeFrame();
		pendingChunks.push(new Float32Array(message.samples));
		let dropped = 0;
		while (pendingChunks.length > 128) {
			pendingChunks.shift();
			chunkOffset = 0;
			dropped += 1;
		}
		recordBridgeFrameDrops(dropped);
	});
	const unsubscribeEnd = nativeAudioApi.onEnd((message) => {
		if (message.captureId !== captureId) return;
		endBridgeStats(message.reason ?? 'ended', message.detail ?? null);
		void cleanup(false);
	});
	const track = destination.stream.getAudioTracks()[0];
	if (!track) {
		await cleanup(false);
		throw new Error('ScriptProcessor fallback produced no audio track');
	}
	return {
		track,
		cleanup,
	};
}
async function createNativeAudioBridge(captureId: string): Promise<NativeAudioBridgeHandle> {
	if (getGeneratorCtor() && getAudioDataCtor()) {
		return createGeneratorBridge(captureId);
	}
	logger.info('Falling back to ScriptProcessor native-audio bridge');
	return createScriptProcessorBridge(captureId);
}
function installPatchIfNeeded(): void {
	if (patched) return;
	if (!isNativeAudioDesktopPlatform()) return;
	if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getDisplayMedia) return;
	originalGetDisplayMedia = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices);
	navigator.mediaDevices.getDisplayMedia = async function patchedGetDisplayMedia(
		this: MediaDevices,
		constraints?: DisplayMediaStreamOptions,
	): Promise<MediaStream> {
		const pendingCapture = armedCapture;
		const stream = await originalGetDisplayMedia!(constraints);
		if (pendingCapture && armedCapture?.captureId === pendingCapture.captureId) {
			armedCapture = null;
			try {
				const handle = await createNativeAudioBridge(pendingCapture.captureId);
				await replaceStreamAudioTrack(stream, handle.track);
				activeBridge = {captureId: pendingCapture.captureId, handle};
				attachNativeAudioCleanup(stream, pendingCapture.captureId, handle);
			} catch (error) {
				logger.warn('Failed to substitute native audio capture into display stream', {
					captureId: pendingCapture.captureId,
					error,
				});
				for (const existingAudioTrack of stream.getAudioTracks()) {
					stream.removeTrack(existingAudioTrack);
					try {
						existingAudioTrack.stop();
					} catch {}
				}
				void getNativeAudioApi()?.stop(pendingCapture.captureId);
			}
		}
		return stream;
	};
	patched = true;
}
function unsupportedPlatformAvailability(): NativeAudioAvailability {
	return {available: false, reason: 'unsupported-platform'};
}
export async function getNativeAudioAvailabilityCached(): Promise<NativeAudioAvailability> {
	if (!isNativeAudioDesktopPlatform()) return unsupportedPlatformAvailability();
	if (!availabilityCache) {
		const nativeAudioApi = getNativeAudioApi();
		availabilityCache = nativeAudioApi
			? nativeAudioApi.getAvailability().catch((error): NativeAudioAvailability => {
					logger.warn('Failed to query native audio availability', {error});
					return {available: false, reason: 'load-failed'};
				})
			: Promise.resolve<NativeAudioAvailability>({available: false, reason: 'unsupported-platform'});
	}
	return availabilityCache;
}
export async function isNativeAudioAvailable(): Promise<boolean> {
	const availability = await getNativeAudioAvailabilityCached();
	return availability.available;
}
export async function armNativeAudioForNextCapture(sourceId: string): Promise<boolean> {
	const electronApi = getElectronAPI();
	const nativeAudioApi = electronApi?.nativeAudio;
	if (!electronApi || !nativeAudioApi || !isNativeAudioDesktopPlatform()) {
		logger.warn('Cannot arm per-window audio capture: not a supported desktop platform', {
			hasElectronApi: Boolean(electronApi),
			hasNativeAudioApi: Boolean(nativeAudioApi),
			platform: electronApi?.platform,
		});
		return false;
	}
	if (!sourceId.startsWith('window:')) {
		logger.warn('Cannot arm per-window audio capture: source is not a window', {sourceId});
		return false;
	}
	if (!(await isNativeAudioAvailable())) {
		const availability = await nativeAudioApi.getAvailability().catch(() => null);
		logger.warn('Cannot arm per-window audio capture: native audio addon unavailable', {
			sourceId,
			availability,
		});
		return false;
	}
	const targetPid = await nativeAudioApi.resolveAudioRootPidForSource(sourceId);
	if (!targetPid) {
		logger.warn('Cannot arm per-window audio capture: could not resolve owning PID', {sourceId});
		return false;
	}
	if (armedCapture) {
		try {
			await nativeAudioApi.stop(armedCapture.captureId);
		} catch (error) {
			logger.warn('Failed to stop previously-armed native audio capture', {
				captureId: armedCapture.captureId,
				error,
			});
		}
		armedCapture = null;
	}
	installPatchIfNeeded();
	let result: NativeAudioStartResult;
	try {
		result = await nativeAudioApi.start({
			targetPid,
			includeProcessTree: electronApi.platform === 'win32' || electronApi.platform === 'darwin',
		});
	} catch (error) {
		logger.warn('Cannot arm per-window audio capture: native audio start failed', {sourceId, targetPid, error});
		return false;
	}
	armedCapture = {
		captureId: result.captureId,
	};
	return true;
}
export function disarmNativeAudio(): void {
	const nativeAudioApi = getNativeAudioApi();
	const bridge = activeBridge;
	if (bridge) {
		activeBridge = null;
		void bridge.handle.cleanup(true).catch((error) => {
			logger.warn('Failed to tear down active native audio bridge', {
				captureId: bridge.captureId,
				error,
			});
		});
	}
	if (!nativeAudioApi || !armedCapture) {
		armedCapture = null;
		return;
	}
	const captureId = armedCapture.captureId;
	armedCapture = null;
	void nativeAudioApi.stop(captureId).catch((error) => {
		logger.warn('Failed to disarm native audio capture', {captureId, error});
	});
}
export function resetNativeAudioAvailabilityCache(): void {
	availabilityCache = null;
}
export function resetNativeAudioCaptureBridgeForTests(): void {
	disarmNativeAudio();
	resetNativeAudioAvailabilityCache();
	if (patched && originalGetDisplayMedia && navigator.mediaDevices) {
		navigator.mediaDevices.getDisplayMedia = originalGetDisplayMedia;
	}
	originalGetDisplayMedia = null;
	patched = false;
	armedCapture = null;
	activeBridge = null;
}
